/** @forge-header
 * @id          kalshiApiPositions
 * @role        Portfolio positions + fills | polymorphic response normalization (5+ shapes)
 * @pattern     adapter
 * @purity      api
 * @units       mixed
 * @money-path  yes
 * @upstream    kalshiApi/client:authenticatedFetch | kalshiApi/types:KalshiOrder,KalshiFill,KalshiPosition
 * @downstream  kalshiApi/index (via createKalshiClient)
 * @exports     getPositions:fn | getFilledOrders:fn | normalizeKalshiPositions:fn
 * @test        untested (integration-level)
 *
 * @invariant   position-sign | position > 0 = YES exposure, position < 0 = NO exposure (when side absent)
 * @danger      normalizeKalshiPositions handles 5+ Kalshi response shapes | test any API version changes
 */

import type { KalshiOrder, KalshiFill, KalshiPosition } from './types';
import { authenticatedFetch } from './client';

/**
 * Convert order to fill (simplified - real API might have separate fill endpoint)
 */
function orderToFill(order: KalshiOrder): KalshiFill {
  const price = order.side === 'yes' ? order.yes_price : order.no_price;
  return {
    fill_id: order.order_id, // Use order_id as fill_id if API doesn't provide separate
    order_id: order.order_id,
    ticker: order.ticker,
    side: order.side,
    action: order.action,
    count: order.remaining_count,
    price: price ?? 0,
    created_at: order.created_time,
  };
}

/**
 * Normalize Kalshi "positions" payloads into our internal KalshiPosition[] shape.
 * Kalshi has had multiple response shapes over time (and can differ by endpoint/version).
 */
export function normalizeKalshiPositions(raw: unknown): KalshiPosition[] {
  const asRecord = (v: unknown): Record<string, unknown> | null =>
    v && typeof v === 'object' ? (v as Record<string, unknown>) : null;
  const toNumber = (v: unknown): number | null => {
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string') {
      const n = Number(v);
      if (Number.isFinite(n)) return n;
    }
    return null;
  };
  const toSide = (v: unknown): 'yes' | 'no' | null => {
    if (typeof v !== 'string') return null;
    const s = v.toLowerCase();
    if (s === 'yes' || s === 'no') return s;
    return null;
  };

  // Unwrap common response envelopes
  const rootRec = asRecord(raw);
  let items: unknown = raw;
  if (rootRec) {
    items =
      // Newer/alternate shapes (seen in prod): { cursor, market_positions, event_positions }
      rootRec.market_positions ??
      rootRec.marketPositions ??
      rootRec.event_positions ??
      rootRec.eventPositions ??
      // Older/common envelopes
      rootRec.positions ??
      rootRec.results ??
      rootRec.data ??
      rootRec.portfolio_positions ??
      rootRec.portfolioPositions ??
      rootRec.position ??
      raw;

    // Sometimes positions are nested like { positions: { positions: [...] } }
    const itemsRec = asRecord(items);
    if (itemsRec && (itemsRec.positions || itemsRec.results || itemsRec.data)) {
      items = itemsRec.positions ?? itemsRec.results ?? itemsRec.data;
    }
  }

  // If the response includes both market_positions + event_positions, merge them.
  // Kalshi's /portfolio/positions in prod commonly returns both arrays.
  if (rootRec) {
    const marketItems = rootRec.market_positions ?? rootRec.marketPositions;
    const eventItems = rootRec.event_positions ?? rootRec.eventPositions;
    if (Array.isArray(marketItems) || Array.isArray(eventItems)) {
      items = [
        ...(Array.isArray(marketItems) ? marketItems : []),
        ...(Array.isArray(eventItems) ? eventItems : []),
      ];
    }
  }

  if (!Array.isArray(items)) {
    return [];
  }

  const out: KalshiPosition[] = [];

  for (const item of items) {
    const rec = asRecord(item);
    if (!rec) continue;

    const ticker =
      (typeof rec.ticker === 'string' && rec.ticker) ||
      (typeof rec.market_ticker === 'string' && rec.market_ticker) ||
      (typeof rec.marketTicker === 'string' && rec.marketTicker) ||
      (typeof rec.event_ticker === 'string' && rec.event_ticker) ||
      (typeof rec.eventTicker === 'string' && rec.eventTicker) ||
      (typeof rec.symbol === 'string' && rec.symbol) ||
      null;

    // Shape A2: signed position without explicit side (common in Kalshi market_positions)
    // Convention: position > 0 => YES exposure, position < 0 => NO exposure.
    if (ticker) {
      const signed = toNumber(rec.position);
      if (signed !== null && signed !== 0 && rec.side === undefined) {
        const inferredSide: 'yes' | 'no' = signed < 0 ? 'no' : 'yes';
        const qty = Math.abs(signed);

        // Try to derive avg price from exposure / qty when avg_price isn't provided.
        const exposure =
          toNumber(
            rec.market_exposure ?? rec.marketExposure ?? rec.total_cost ?? rec.totalCost ?? rec.cost
          ) ??
          toNumber(
            rec.market_exposure_dollars ??
              rec.marketExposureDollars ??
              rec.total_cost_dollars ??
              rec.totalCostDollars ??
              rec.cost_dollars ??
              rec.costDollars
          );

        const avg =
          toNumber(rec.average_price ?? rec.avg_price ?? rec.avgPrice ?? rec.averagePrice) ??
          (exposure !== null && qty > 0 ? exposure / qty : 0);

        const realized =
          toNumber(
            rec.realized_pnl ??
              rec.realizedPnl ??
              rec.realized_pnl_dollars ??
              rec.realizedPnlDollars
          ) ?? 0;

        out.push({
          ticker,
          side: inferredSide,
          position: qty,
          average_price: avg,
          realized_pnl: realized,
        });
        continue;
      }
    }

    // Shape A: explicit side + position
    const side = toSide(rec.side);
    const position = toNumber(rec.position ?? rec.quantity ?? rec.count);

    if (ticker && side && position !== null) {
      const avg =
        toNumber(
          rec.average_price ??
            rec.avg_price ??
            rec.avgPrice ??
            rec.averagePrice ??
            rec.entry_price ??
            rec.entryPrice
        ) ?? 0;
      const realized =
        toNumber(
          rec.realized_pnl ?? rec.realizedPnl ?? rec.realized_pnl_dollars ?? rec.realizedPnlDollars
        ) ?? 0;

      out.push({
        ticker,
        side,
        position,
        average_price: avg,
        realized_pnl: realized,
      });
      continue;
    }

    // Shape B: combined yes/no positions per ticker
    if (ticker) {
      const yesPos = toNumber(
        rec.yes_position ??
          rec.yesPosition ??
          rec.yes_qty ??
          rec.yesQty ??
          rec.yes_quantity ??
          rec.yesQuantity ??
          rec.yes_contracts ??
          rec.yesContracts
      );
      const noPos = toNumber(
        rec.no_position ??
          rec.noPosition ??
          rec.no_qty ??
          rec.noQty ??
          rec.no_quantity ??
          rec.noQuantity ??
          rec.no_contracts ??
          rec.noContracts
      );

      if (yesPos !== null) {
        const yesAvg =
          toNumber(
            rec.yes_average_price ??
              rec.yes_avg_price ??
              rec.yesAvgPrice ??
              rec.average_price ??
              rec.avg_price
          ) ?? 0;
        const yesRealized =
          toNumber(
            rec.yes_realized_pnl ?? rec.yesRealizedPnl ?? rec.realized_pnl ?? rec.realizedPnl
          ) ?? 0;
        out.push({
          ticker,
          side: 'yes',
          position: yesPos,
          average_price: yesAvg,
          realized_pnl: yesRealized,
        });
      }

      if (noPos !== null) {
        const noAvg =
          toNumber(
            rec.no_average_price ??
              rec.no_avg_price ??
              rec.noAvgPrice ??
              rec.average_price ??
              rec.avg_price
          ) ?? 0;
        const noRealized =
          toNumber(
            rec.no_realized_pnl ?? rec.noRealizedPnl ?? rec.realized_pnl ?? rec.realizedPnl
          ) ?? 0;
        out.push({
          ticker,
          side: 'no',
          position: noPos,
          average_price: noAvg,
          realized_pnl: noRealized,
        });
      }
    }
  }

  // Filter out zero positions to match what users expect to see in a Positions table.
  return out.filter((p) => Number.isFinite(p.position) && p.position !== 0);
}

/**
 * Get positions
 */
export async function getPositions(): Promise<KalshiPosition[]> {
  try {
    const response = await authenticatedFetch('GET', '/trade-api/v2/portfolio/positions');
    const data = await response.json();

    const positions = normalizeKalshiPositions(data);
    return positions;
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    if (
      message.includes('API error: 401') ||
      message.includes('API error: 403') ||
      message.includes('"code":"authentication_error"') ||
      message.includes('"code": "authentication_error"')
    ) {
      throw error;
    }
    console.warn('Failed to fetch positions:', error);
    return [];
  }
}

/**
 * Get filled orders
 */
export async function getFilledOrders(
  params: { cursor?: string; limit?: number } = {}
): Promise<KalshiFill[]> {
  try {
    const query: Record<string, string> = { status: 'filled' };
    if (params.cursor) query.cursor = params.cursor;
    if (params.limit) query.limit = params.limit.toString();

    const response = await authenticatedFetch('GET', '/trade-api/v2/portfolio/orders', query);
    const data = await response.json();

    // Handle different response formats
    if (Array.isArray(data)) {
      return data.filter((o: KalshiOrder) => o.status === 'filled').map(orderToFill);
    }
    if (data.orders && Array.isArray(data.orders)) {
      return data.orders.filter((o: KalshiOrder) => o.status === 'filled').map(orderToFill);
    }
    if (data.results && Array.isArray(data.results)) {
      return data.results.filter((o: KalshiOrder) => o.status === 'filled').map(orderToFill);
    }

    return [];
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    if (
      message.includes('API error: 401') ||
      message.includes('API error: 403') ||
      message.includes('"code":"authentication_error"') ||
      message.includes('"code": "authentication_error"')
    ) {
      throw error;
    }
    console.warn('Failed to fetch filled orders:', error);
    return [];
  }
}
